import {Paths} from "./Paths";
import {Menu, MenuItem, Sidebar, SubMenu} from "react-pro-sidebar";
import React, {useEffect} from "react";
import ChevronIcon from "../../assets/ChevronIcon";
import {Link, useLocation} from "react-router-dom";
import AppNavIcon from "../../assets/AppNavIcon";
import './sidebar.css'
import {ButtonComponent} from "../button/ButtonComponent";
import MenuIcon from "../../assets/MenuIcon";
import FooterLightIcon from "../../assets/FooterLightIcon";
import FooterDarkIcon from "../../assets/FooterDarkIcon";
import {getNextColor, LightMode} from "../../theme/Mode";
import FooterColorIcon from "../../assets/FooterColorIcon";
import {trackPageView} from "../../utils/Analytics";
import {ThemeColor} from "../../../home/components/homecard/HomeCardComponent";
import FooterContactIcon from "../../assets/FooterContactIcon";

function checkIfMobile() {
    const currentWidth = document.documentElement.clientWidth;
    return currentWidth < 768;
}

export default function SidebarComponent({toggleLightMode, lightMode, toggleColorMode, colorMode}) {
    useEffect(() => {
        const handleResize = () => {
            const newIsMobile = checkIfMobile();
            setIsMobile(newIsMobile);
        };
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    const location = useLocation()
    const [isMobile, setIsMobile] = React.useState(checkIfMobile());
    const useDesktopSidebar = !isMobile && location.pathname !== Paths.home.path

    useEffect(() => {
        trackPageView(location);
    }, [])

    useEffect(() => {
        trackPageView(location)
    }, [location]);

    return (<>
        {useDesktopSidebar &&
            <DesktopSidebar toggleLightMode={toggleLightMode} lightMode={lightMode} toggleColorMode={toggleColorMode}
                            colorMode={colorMode}/>}
        {!useDesktopSidebar &&
            <MobileSidebar toggleLightMode={toggleLightMode} lightMode={lightMode} toggleColorMode={toggleColorMode}
                           colorMode={colorMode}/>}
    </>)
}

function MenuComponent({location, onClick, lightMode, toggleLightMode, colorMode, toggleColorMode}) {
    const topLevelNavKeys = Object.keys(Paths)
    const isLightMode = lightMode === LightMode.light
    return (<div className="sidebarContainer">
        <div className="sidebarHeader">
            <span className="sidebarHeaderIcon"><AppNavIcon/></span>ShesAMobileDev
        </div>
        <Menu
            menuItemStyles={{
                button: ({active}) => {
                    return {
                        backgroundColor: (active ? "var(--sidebar-nav-active)" : "var(--sidebar-default)"),
                        color: (active ? "var(--primary-text-emphasis)" : "var(--on-bg)"),
                        '&:hover': {
                            backgroundColor: "var(--sidebar-nav-hover)", color: "var(--info-text-emphasis)"
                        },
                        fontSize: "20px"
                    };
                }
            }}
            renderExpandIcon={({open}) => <ChevronIcon
                className={"chevronIcon sidebar" + (open ? " expanded" : "")}/>}
        >
            {topLevelNavKeys.map(topLevelNavKey => {
                const navObject = Paths[topLevelNavKey]
                if (navObject.nested.length !== 0) {
                    const nestedKeys = Object.keys(navObject.nested)
                    const defaultIsOpen = getDefaultSubMenuState(location.pathname, navObject, nestedKeys)
                    return (<SubMenu label={navObject.name}
                                     icon={navObject.icon()}
                                     defaultOpen={defaultIsOpen}
                                     key={navObject.name}
                    >
                        {nestedKeys.map(nestedKey => {
                            return (<MenuItem
                                onClick={() => onClick()}
                                key={navObject.nested[nestedKey].path}
                                active={location.pathname === navObject.nested[nestedKey].path}
                                component={<Link to={navObject.nested[nestedKey].path}/>}
                                icon={navObject.nested[nestedKey].icon()}>
                                {navObject.nested[nestedKey].name}
                            </MenuItem>)
                        })}
                    </SubMenu>)
                } else {
                    return (<MenuItem
                        key={navObject.path}
                        onClick={() => onClick()}
                        active={location.pathname === navObject.path}
                        component={<Link to={navObject.path}/>}
                        icon={navObject.icon()}>
                        {navObject.name}
                    </MenuItem>)
                }
            })}
        </Menu>
        <div className="sidebarFooter">
            <a href="https://www.reddit.com/r/ShesAMobileDev/">
                <ButtonComponent icon={<FooterContactIcon/>}
                                 analyticsName="Sidebar Footer Contact"
                                 color={ThemeColor.Secondary}
                                 ariaLabel="Visit Reddit Forum to stay in touch"
                                 onClick={() => {
                                 }}/>
            </a>
            <div className="footerFill"/>
            <ButtonComponent color={isLightMode ? ThemeColor.Info : ThemeColor.Light}
                             analyticsName={"Sidebar Footer Light Mode " + (isLightMode ? "dark" : "light")}
                             ariaLabel={"Toggle " + (isLightMode ? "Dark" : "Light") + " Mode"}
                             icon={isLightMode ? <FooterLightIcon/> : <FooterDarkIcon/>}
                             onClick={() => toggleLightMode(isLightMode ? LightMode.dark : LightMode.light)}/>
            <ButtonComponent icon={<FooterColorIcon/>}
                             analyticsName={"Sidebar Footer Color Mode " + getNextColor(colorMode)}
                             color={ThemeColor.Primary}
                             ariaLabel="Change Theme Color"
                             onClick={() => toggleColorMode(getNextColor(colorMode))}/>
        </div>
    </div>)
}

function getDefaultSubMenuState(locationPath, navObject, nestedKeys) {
    for (const key of nestedKeys) {
        const nestedObject = navObject.nested[key]
        if (locationPath === nestedObject.path) return true
    }
    return false
}

function MobileSidebar({toggleLightMode, lightMode, toggleColorMode, colorMode}) {
    const location = useLocation();
    const [toggled, setToggled] = React.useState(false);

    const isOnHomeScreen = location.pathname === Paths.home.path;
    let navToggleColor = "bgInfo";
    if (isOnHomeScreen) {
        navToggleColor = "light"
    }

    return (<Sidebar backgroundColor="var(--sidebar-default)"
                     breakPoint="all"
                     toggled={toggled}
                     onBackdropClick={() => setToggled(false)}
                     width="21rem">
        <ButtonComponent className="navToggle"
                         color={navToggleColor}
                         ariaLabel={(toggled ? "Close" : "Open") + " menu"}
                         icon={<MenuIcon/>}
                         analyticsName="Nav Toggle"
                         onClick={() => setToggled(!toggled)}/>
        <div className="navScroll">
            <MenuComponent location={location} toggleColorMode={toggleColorMode} colorMode={colorMode}
                           lightMode={lightMode} toggleLightMode={toggleLightMode}
                           onClick={() => setToggled(false)}/>
        </div>
    </Sidebar>)
}

function DesktopSidebar({toggleLightMode, lightMode, toggleColorMode, colorMode}) {
    const location = useLocation();

    return (<Sidebar backgroundColor="var(--sidebar-default)"
                     breakPoint="md"
                     width="21rem">
        <MenuComponent location={location} toggleColorMode={toggleColorMode} colorMode={colorMode}
                       lightMode={lightMode} toggleLightMode={toggleLightMode}
                       onClick={() => {
                       }}/>
    </Sidebar>);
}
